// 1. Third-party libraries
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

// 2. Components
import UserManagementFormDialog from "./UserManagementFormDialog";

// 6. Services
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// 9. Config
import { appConfig } from "../../config/appConfig";

// 12. Asset components
import visiable from "../../assets/visiable.svg";
import edit from "../../assets/edit.svg";
import delete1 from "../../assets/delete.svg";
import add from "../../assets/add.svg";

// 13. Styles
import "./UserManagement.scss";
import Spinner from "../../components/spinner/Spinner";

type AdminEntry = {
  [x: string]: any;
  id: number;
  username: string;
  email: string;
  password: string;
  phonenumber: string;
  role: string;
};
const Usermanagement = () => {
  // useState
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [filterList, setFilterList] = useState<AdminEntry[]>([]);
  const [formData, setFormData] = useState<AdminEntry>({
    id: 0,
    username: "",
    email: "",
    password: "",
    phonenumber: "",
    role: "System Admin", // default value
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [searchUserName, setSearchUserName] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [role, setRole] = useState<string>("");
  const [page, setPage] = useState(0);
  const [loading, setloading] = useState(false);
  const rowsPerPage = 7;

  // useNavigate
  const navigate = useNavigate();

  // useEffect
  useEffect(() => {
    const storedRole = getStorageItemLocal("role");
    if (storedRole) setRole(JSON.parse(storedRole));
  }, []);

  useEffect(() => {
    const savedData = localStorage.getItem("CategoryList");
    if (savedData) {
      const parsedData: AdminEntry[] = JSON.parse(savedData);
      setDataList(parsedData);
      setFilterList(parsedData);

      if (!savedData || parsedData.length === 0) {
        setEditingIndex(null);
      }
      // setDataList(JSON.parse(savedData));
    }

    setSearchUserName("");
    setSearchRole("");
    setSearchPhone("");
  }, []);
  useEffect(() => {
    const filteredData = dataList.filter((item) => {
      const matchesUsername = item.username
        ?.toLowerCase()
        .includes(searchUserName.trim().toLowerCase());
      const matchesRole = (item.role ?? "")
        .toLowerCase()
        .includes(searchRole.trim().toLowerCase());
      const matchesPhone = item.phonenumber?.includes(searchPhone.trim());

      return matchesUsername && matchesRole && matchesPhone;
    });
    setFilterList(filteredData);
  }, [searchUserName, searchRole, searchPhone]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setloading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;

      const response = await fetch(appConfig.serviceUrls.saveLoginEntries, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch data");
      const data: AdminEntry[] = await response.json();

      setDataList(data || []);
      setFilterList(data || []);
      //setDataList(formattedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      //setDataList([]);
    } finally {
      setloading(false);
    }
  };

  const resetForm = () => {
    //Reset Form
    setFormData({
      id: dataList.length + 1, //Increment ID
      username: "",
      email: "",
      password: "",
      phonenumber: "",
      role: "systemadmin", // ✅ default value
    });
    setEditingIndex(null);
  };
  //Add New Record
  const handleAdd = () => {
    setEditingIndex(null);
    setShowForm(true);
    // resetForm();// Clear form and show it
    setFormData({
      id: 0,
      username: "",
      email: "",
      password: "",
      phonenumber: "",
      role: "systemadmin", // ✅ default value
    });
    setSearchUserName("");
    setSearchRole("");
    setSearchPhone("");
  };

  const handleEdit = (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }
    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }
    const entry = dataList[index];
    setFormData({
      ...selectedEntry,
    });
    setEditingIndex(entry.id);
    setShowForm(true);
  };
  //Delete a record
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure want to delete this entry?")) return;
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(
        `${appConfig.serviceUrls.saveLoginEntries}/${id}`,
        {
          method: "DELETE",

          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to delete");

      fetchData();
      if (editingIndex !== null && dataList[editingIndex]?.id === id) {
        setEditingIndex(null);
      }
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  return (
    <Box className="container-wraper">
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography className="bold-header-text">
          <b>User Management</b>{" "}
        </Typography>
        <Button onClick={handleAdd} className="custom-button">
          <img src={add} alt="visiable" className="img-style" />{" "}
          <Typography className="btn-text">Add</Typography>
        </Button>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search by Username"
          size="small"
          value={searchUserName}
          onChange={(e) => setSearchUserName(e.target.value)}
          sx={{ width: "230px", height: "40px", background: "#fff" }}
        />
        <TextField
          select
          label="Search by Role"
          size="small"
          value={searchRole}
          onChange={(e) => setSearchRole(e.target.value)}
          // sx={{ minWidth: 180 }}
          sx={{ width: "230px", height: "40px", background: "#fff" }}
        >
          <MenuItem value="">All Roles</MenuItem>
          <MenuItem value="System Admin">System Admin</MenuItem>
          <MenuItem value="Admin User">Admin User</MenuItem>
        </TextField>
      </Stack>
      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <></>
      )}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
        {/*Data Table*/}

        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
          }}
        >
          {dataList?.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow className="custom-table-head-row">
                  <TableCell>
                    <b>User Name</b>
                  </TableCell>
                  <TableCell>
                    <b>Email</b>
                  </TableCell>
                  <TableCell>
                    <b>Phone Number</b>
                  </TableCell>

                  <TableCell>
                    <b>Password</b>
                  </TableCell>
                  <TableCell>
                    <b>Role</b>
                  </TableCell>
                  <TableCell>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filterList
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => (
                    <TableRow key={index} className="custom-table-row">
                      <TableCell>{item.username || "\u00A0"}</TableCell>

                      <TableCell>{item.email || "\u00A0"}</TableCell>

                      <TableCell>{item.phonenumber || "\u00A0"}</TableCell>

                      <TableCell>
                        ***********{" "}
                        <img
                          src={visiable}
                          alt="visiable"
                          className="img-style"
                          onClick={() => {
                            handleEdit(page * rowsPerPage + index);
                          }}
                        />
                      </TableCell>
                      <TableCell>{item.role ?? "null"}</TableCell>

                      <TableCell align="left">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => {
                            handleEdit(page * rowsPerPage + index);
                          }}
                        >
                          <img
                            src={edit}
                            alt="visiable"
                            className="img-style"
                          />
                        </IconButton>

                        {role === "System Admin" && (
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => {
                              handleDelete(item.id);
                            }}
                          >
                            <img
                              src={delete1}
                              alt="visiable"
                              className="img-style"
                            />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "1rem",
              }}
            >
              <Typography align="center">No records found.</Typography>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleAdd}
              >
                <Add fontSize="small" />
              </Button>
            </Box>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            p={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1rem",
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
              }}
            >
              Showing {page * rowsPerPage + 1} to{" "}
              {page * rowsPerPage + rowsPerPage} of {filterList.length} entries
            </Typography>
            <Pagination
              count={Math.ceil(filterList.length / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </TableContainer>
      </Box>
      <UserManagementFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        fetchData={fetchData}
        resetForm={resetForm}
        formData={formData}
        setFormData={setFormData}
        editingIndex={editingIndex}
        setShowForm={setShowForm}
        setEditingIndex={setEditingIndex}
        dataList={dataList}
      />
    </Box>
  );
};

export default Usermanagement;
